import { defineMessages, type MessageDescriptor } from 'react-intl';

export type EventTypeMessages = {
	absolute: MessageDescriptor;
	relative: MessageDescriptor;
	standalone: MessageDescriptor;
};

export const clickedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} clicked on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - clicked',
		id: 'recommended.eventTypeMessage.absolute.clicked',
	},
	relative: {
		defaultMessage: '{collaborator} clicked {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - clicked',
		id: 'recommended.eventTypeMessage.relative.clicked',
	},
	standalone: {
		defaultMessage: '{collaborator} clicked',
		description: 'action taken by the user on this object - clicked',
		id: 'recommended.eventTypeMessage.clicked',
	},
});

export const commentedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} commented on {absoluteTime}',
		description: 'action taken on this object with absolute timestamp - comment added',
		id: 'recommended.eventTypeMessage.absolute.commented',
	},
	relative: {
		defaultMessage: '{collaborator} commented {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - comment added',
		id: 'recommended.eventTypeMessage.relative.commented',
	},
	standalone: {
		defaultMessage: '{collaborator} commented',
		description: 'action taken by the user on this object - comment added',
		id: 'recommended.eventTypeMessage.commented',
	},
});

export const editedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} edited on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - edited',
		id: 'recommended.eventTypeMessage.absolute.edited',
	},
	relative: {
		defaultMessage: '{collaborator} edited {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - edited',
		id: 'recommended.eventTypeMessage.relative.edited',
	},
	standalone: {
		defaultMessage: '{collaborator} edited',
		description: 'action taken by the user on this object - edited',
		id: 'recommended.eventTypeMessage.edited',
	},
});

export const viewedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} viewed on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - viewed',
		id: 'recommended.eventTypeMessage.absolute.viewed',
	},
	relative: {
		defaultMessage: '{collaborator} viewed {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - viewed',
		id: 'recommended.eventTypeMessage.relative.viewed',
	},
	standalone: {
		defaultMessage: '{collaborator} viewed',
		description: 'action taken by the user on this object - viewed',
		id: 'recommended.eventTypeMessage.viewed',
	},
});

export const mentionedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} were mentioned on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - were mentioned',
		id: 'recommended.eventTypeMessage.absolute.wereMentioned',
	},
	relative: {
		defaultMessage: '{collaborator} were mentioned {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - were mentioned',
		id: 'recommended.eventTypeMessage.relative.wereMentioned',
	},
	standalone: {
		defaultMessage: '{collaborator} were mentioned',
		description: 'action taken by the user on this object - were mentioned',
		id: 'recommended.eventTypeMessage.wereMentioned',
	},
});

export const sharedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} shared on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - shared',
		id: 'recommended.eventTypeMessage.absolute.shared',
	},
	relative: {
		defaultMessage: '{collaborator} shared {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - shared',
		id: 'recommended.eventTypeMessage.relative.shared',
	},
	standalone: {
		defaultMessage: '{collaborator} shared',
		description: 'action taken by the user on this object - shared',
		id: 'recommended.eventTypeMessage.shared',
	},
});

export const reactedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} reacted on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - reacted',
		id: 'recommended.eventTypeMessage.absolute.reacted',
	},
	relative: {
		defaultMessage: '{collaborator} reacted {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - reacted',
		id: 'recommended.eventTypeMessage.relative.reacted',
	},
	standalone: {
		defaultMessage: '{collaborator} reacted',
		description: 'action taken by the user on this object - reacted',
		id: 'recommended.eventTypeMessage.reacted',
	},
});

export const readEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} read on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - read',
		id: 'recommended.eventTypeMessage.absolute.read',
	},
	relative: {
		defaultMessage: '{collaborator} read {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - read',
		id: 'recommended.eventTypeMessage.relative.read',
	},
	standalone: {
		defaultMessage: '{collaborator} read',
		description: 'action taken by the user on this object - read',
		id: 'recommended.eventTypeMessage.read',
	},
});

export const createdEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} created on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - created',
		id: 'recommended.eventTypeMessage.absolute.created',
	},
	relative: {
		defaultMessage: '{collaborator} created {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - created',
		id: 'recommended.eventTypeMessage.relative.created',
	},
	standalone: {
		defaultMessage: '{collaborator} created',
		description: 'action taken by the user on this object - created',
		id: 'recommended.eventTypeMessage.created',
	},
});

export const interactedEventTypeMessages: EventTypeMessages = defineMessages({
	absolute: {
		defaultMessage: '{collaborator} interacted on {absoluteTime}',
		description: 'action taken by the user on this object with absolute timestamp - interacted',
		id: 'recommended.eventTypeMessage.absolute.interacted',
	},
	relative: {
		defaultMessage: '{collaborator} interacted {relativeTime}',
		description: 'action taken by the user on this object with relative timestamp - interacted',
		id: 'recommended.eventTypeMessage.relative.interacted',
	},
	standalone: {
		defaultMessage: '{collaborator} interacted',
		description: 'action taken by the user on this object - interacted',
		id: 'recommended.eventTypeMessage.interacted',
	},
});

export const messages: {
	you: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	collaborator: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	you: {
		id: 'recommended.you',
		defaultMessage: 'You',
		description:
			'Label to indicate the user themselves in event messages, e.g. "You commented on this page."',
	},
	collaborator: {
		id: 'recommended.collaborator',
		defaultMessage: 'Collaborator',
		description:
			'Label to indicate a collaborator in event messages, e.g. "Collaborator commented on this page."',
	},
});
